import { ActionButton, App, Button, Form, Input, InputNumber, Modal, Select, Switch } from '@wanjianshi/admin-ui';
import type { AdminAction } from '@wanjianshi/admin-ui';
import type { ButtonProps } from 'antd';
import type { ReactNode } from 'react';
import { useEffect, useMemo, useState } from 'react';

export interface EntityFormOption {
  label: string;
  value: string | number | boolean;
}

export interface EntityFormField {
  name: string;
  label: string;
  type?: 'text' | 'password' | 'textarea' | 'number' | 'switch' | 'select' | 'multi-select' | 'json';
  required?: boolean;
  placeholder?: string;
  options?: EntityFormOption[];
  min?: number;
  max?: number;
  minLength?: number;
  maxLength?: number;
  hidden?: boolean;
}

function prepareInitialValue(value: unknown, field: EntityFormField): unknown {
  if (field.type === 'json' && value !== undefined && value !== null && typeof value !== 'string') {
    return JSON.stringify(value, null, 2);
  }
  return value;
}

function normalizeValues(values: Record<string, unknown>, fields: EntityFormField[]) {
  const result = { ...values };
  fields.forEach(field => {
    if (field.type !== 'json') return;
    const raw = result[field.name];
    if (raw === '' || raw === undefined || raw === null) {
      result[field.name] = undefined;
      return;
    }
    if (typeof raw === 'string') result[field.name] = JSON.parse(raw);
  });
  return result;
}

export function EntityFormModal({
  title,
  description,
  buttonLabel,
  fields,
  initialValues,
  reasonRequired = false,
  onSubmit,
  buttonProps,
  buttonAction,
}: {
  title: string;
  description?: ReactNode;
  buttonLabel: string;
  fields: EntityFormField[];
  initialValues?: Record<string, unknown>;
  reasonRequired?: boolean;
  onSubmit: (values: Record<string, unknown>, reason: string) => Promise<void>;
  buttonProps?: ButtonProps;
  buttonAction?: AdminAction;
}) {
  const { message } = App.useApp();
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm();
  const normalizedInitialValues = useMemo(
    () => Object.fromEntries(fields.map(field => [field.name, prepareInitialValue(initialValues?.[field.name], field)])),
    [fields, initialValues],
  );
  useEffect(() => {
    if (open) form.setFieldsValue(normalizedInitialValues);
  }, [form, normalizedInitialValues, open]);
  const submit = async () => {
    let values: Record<string, unknown>;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    setSubmitting(true);
    try {
      const reason = String(values.__operation_reason ?? '').trim();
      delete values.__operation_reason;
      await onSubmit(normalizeValues(values, fields), reason);
      setOpen(false);
      form.resetFields();
    } catch (error) {
      message.error(error instanceof Error ? error.message : '操作失败，请稍后重试');
    } finally {
      setSubmitting(false);
    }
  };
  return (
    <>
      {buttonAction ? (
        <ActionButton action={buttonAction} {...buttonProps} onClick={() => setOpen(true)}>{buttonLabel}</ActionButton>
      ) : (
        <Button {...buttonProps} onClick={() => setOpen(true)}>{buttonLabel}</Button>
      )}
      <Modal
        open={open}
        title={title}
        okText="保存"
        cancelText="取消"
        confirmLoading={submitting}
        onOk={() => void submit()}
        onCancel={() => setOpen(false)}
        destroyOnHidden
      >
        {description ? (
          <div style={{ marginBottom: 16 }}>{description}</div>
        ) : null}
        <Form form={form} layout="vertical" initialValues={normalizedInitialValues}>
          {fields.filter(field => !field.hidden).map(field => {
            const rules = [
              ...(field.required ? [{ required: true, message: `请填写${field.label}` }] : []),
              ...(field.minLength !== undefined ? [{ min: field.minLength, message: `${field.label}不能少于 ${field.minLength} 个字符` }] : []),
              ...(field.maxLength !== undefined ? [{ max: field.maxLength, message: `${field.label}不能超过 ${field.maxLength} 个字符` }] : []),
            ];
            let control;
            if (field.type === 'password') control = <Input.Password placeholder={field.placeholder} autoComplete="new-password" maxLength={field.maxLength} />;
            else if (field.type === 'textarea' || field.type === 'json') control = <Input.TextArea rows={field.type === 'json' ? 6 : 3} placeholder={field.placeholder} />;
            else if (field.type === 'number') control = <InputNumber style={{ width: '100%' }} min={field.min} max={field.max} />;
            else if (field.type === 'switch') control = <Switch />;
            else if (field.type === 'select' || field.type === 'multi-select') control = <Select mode={field.type === 'multi-select' ? 'multiple' : undefined} options={field.options} allowClear />;
            else control = <Input placeholder={field.placeholder} />;
            return <Form.Item key={field.name} name={field.name} label={field.label} rules={rules.length ? rules : undefined} valuePropName={field.type === 'switch' ? 'checked' : 'value'}>{control}</Form.Item>;
          })}
          {reasonRequired ? (
            <Form.Item name="__operation_reason" label="操作原因" rules={[{ required: true, message: '请输入操作原因' }]}>
              <Input.TextArea rows={3} placeholder="说明本次变更的原因，内容会进入操作审计" />
            </Form.Item>
          ) : null}
        </Form>
      </Modal>
    </>
  );
}
